import { z } from 'zod';
import { type NestedObjectOf } from '../interfaces.js';
import { fromKeys, isFunction, isString, keys, mapValues, omit, pickBy, entries } from 'remeda';
import { DurationLikeObject, DurationUnit } from './dates.js';
import { Duration } from 'luxon';

/**
 * Combines a Zod schema with descriptions for OpenAI json schema export
 * @param schema - The base Zod schema
 * @param descriptions - an object matching the shape of the schema, with descriptions of fields as values
 */
export function addDescriptionsToSchema<S extends z.ZodObject>(
  schema: S,
  descriptions?: NestedObjectOf<string> | string | null
): S {
  if (!descriptions) {
    return schema;
  }
  if (typeof descriptions === 'string') {
    return schema.describe(descriptions);
  } else if (!(schema instanceof z.ZodObject)) {
    // TODO: log a warning that the descriptions format isn't valid
    return schema;
  }

  const extraFieldDescriptions = omit(descriptions, keys(schema.shape));

  return z.object({
    ...addExtraDescriptions(extraFieldDescriptions).shape,
    ...schema.shape,
    ...mapValues(schema.shape, (schemaProp, fieldName) => {
      return addDescription(schemaProp, descriptions[fieldName] ?? null);
    }),
  }) as unknown as S;

  function addExtraDescriptions(descriptionValue: NestedObjectOfStrings): z.ZodObject {
    return z.object(
      mapValues(descriptionValue, (val) => {
        if (!val) {
          return;
        }
        return isString(val) ? z.string().describe(val) : addExtraDescriptions(val);
      })
    );
  }

  function addDescription<S extends z.ZodType>(
    schemaProp: S,
    descriptionValue: NestedObjectOfStrings | string | null
  ): S {
    if (!descriptionValue) {
      return schemaProp;
    }
    if (typeof descriptionValue === 'string') {
      return schemaProp.describe(descriptionValue);
    }
    if (schemaProp instanceof z.ZodObject) {
      return addDescriptionsToSchema(schemaProp, descriptionValue);
    }
    if (schemaProp instanceof z.ZodArray) {
      const element = schemaProp.element || schemaProp.unwrap();
      if (element instanceof z.ZodType) {
        return z.array(addDescription(element, descriptionValue)) as unknown as S;
      }
    }
    return schemaProp;
  }
}

function nestedObjectSchema<T extends z.ZodType<string | number | boolean | undefined | null>>(
  baseSchema: T
) {
  const schema: z.ZodType<NestedObjectOf<z.infer<T>>> = z.record(
    z.string(),
    baseSchema.or(z.lazy(() => schema))
  );
  return schema;
}

export type NestedObjectOfStrings = NestedObjectOf<string>;
export const NestedObjectOfStrings: z.ZodType<NestedObjectOfStrings> = nestedObjectSchema(
  z.string()
);

// Util to merge two zod schemas. Not accurate type-wise, but recursively adds properties
export function mergeSchemas<T extends z.ZodObject>(
  base: T,
  schemaToMerge?: z.ZodObject | null
): T {
  if (!schemaToMerge) {
    return base;
  }
  const commonProperties = keys(schemaToMerge.shape).filter((key) => key in base.shape);
  const baseOnlyShape = pickBy(base.shape, (_val, key) => !(key in schemaToMerge.shape));
  const mergeOnlyShape = pickBy(schemaToMerge.shape, (_val, key) => !(key in base.shape));
  return z.object({
    ...baseOnlyShape,
    ...mergeOnlyShape,
    ...fromKeys(commonProperties, (key) => {
      const baseProp: z.ZodType = base.shape[key];
      const mergeProp: z.ZodType = schemaToMerge.shape[key];
      if (baseProp instanceof z.ZodObject && mergeProp instanceof z.ZodObject) {
        return mergeSchemas(baseProp, mergeProp);
      } else {
        return mergeProp;
      }
    }),
  }) as T;
}

/**
 * Convert a Zod schema to JSON Schema (draft-7) for OpenAI.
 *
 * @param mode
 *  - `'strict'` (default): For structured outputs. All properties required, optionals become `anyOf: [type, null]`.
 *  - `'tools'`:  For tool call parameters. Optional fields omitted from `required`, no null wrapping.
 */
export function zodToJsonSchema(schema: z.ZodType, mode: 'strict' | 'tools' = 'strict') {
  return z.toJSONSchema(schema, {
    target: 'draft-7',

    override({ zodSchema, jsonSchema }) {
      if (
        'description' in zodSchema &&
        isString(zodSchema.description) &&
        jsonSchema.description !== zodSchema.description
      ) {
        // Add the description manually in case it is omitted during conversion
        jsonSchema.description = zodSchema.description;
      }

      const isOptional = z.safeParse(zodSchema, undefined).success;

      if (mode === 'strict') {
        // Strict mode: convert optional fields into unions with `null` to support OpenAI structured output requirements
        if (isOptional) {
          if (!jsonSchema.anyOf) {
            const copy: typeof jsonSchema = {};
            for (const key in jsonSchema) {
              if (key !== 'description') {
                copy[key] = jsonSchema[key];
                delete jsonSchema[key];
              }
            }
            jsonSchema.anyOf = [copy, { type: 'null' }];
          } else if (!jsonSchema.anyOf.find((item) => item.type === 'null')) {
            jsonSchema.anyOf.push({ type: 'null' });
          }
        }
        // All properties of objects must be required
        if (jsonSchema.properties) {
          const propList = keys(jsonSchema.properties);
          if (jsonSchema.required?.length !== propList.length) {
            jsonSchema.required = propList;
          }
        }
        // Additional properties are not allowed in strict mode
        if (jsonSchema.additionalProperties) {
          jsonSchema.additionalProperties = false;
        }
      } else {
        // Tools mode: only actually-required fields in `required`, no null wrapping
        if (jsonSchema.properties) {
          const requiredFields = keys(jsonSchema.properties).filter((key) => {
            const prop = (schema as z.ZodObject).shape?.[key];
            return prop ? !z.safeParse(prop, undefined).success : true;
          });
          jsonSchema.required = requiredFields.length > 0 ? requiredFields : undefined;
        }
      }
    },
  });
}
export type JSONSchema = Required<ReturnType<typeof zodToJsonSchema>>['anyOf'][number];

// Unwraps optional, nullable and default wrappers, and arrays unless the caller needs to see them (a path walk does).
export function unwrapSchema<T extends z.core.$ZodType>(
  schema: T,
  unwrapArrays = true,
  levels = 0
) {
  if (levels > 10) {
    return schema;
  }
  if (
    schema instanceof z.ZodNullable ||
    schema instanceof z.ZodOptional ||
    schema instanceof z.ZodDefault ||
    (unwrapArrays && schema instanceof z.ZodArray)
  ) {
    return unwrapSchema(schema.unwrap(), unwrapArrays, levels + 1);
  }
  return schema;
}

export function extractDuration(duration: DurationLikeObject): [DurationUnit, number][] {
  return entries(duration);
}

export function durationToValue(
  duration: DurationLikeObject | undefined,
  unit: DurationUnit
): number {
  if (!duration) return 0;
  const luxonDuration = Duration.fromObject(duration);
  return luxonDuration.as(unit);
}

export function valueToDuration(
  value: number,
  unit: DurationUnit,
  originalDuration: DurationLikeObject
): DurationLikeObject {
  const entries = extractDuration(originalDuration);

  // Convert input value to a Duration
  const luxonDuration = Duration.fromObject({ [unit]: value });

  // Convert to original unit(s)
  const result: DurationLikeObject = {};
  for (const [originalUnit, _value] of entries) {
    result[originalUnit] = luxonDuration.as(originalUnit);
  }

  return result;
}
